import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Search, Filter, X, Users, User, Calendar, Tag } from "lucide-react";
import { cn } from "@/lib/utils";
import { CATEGORY_LABEL, OBJECTIVE_CATEGORIES, OBJECTIVE_STATUSES, STATUS_CONFIG } from "./okrConstants";
import { useOkrCycles } from "@/hooks/useOkrCycles";
import { ObjectiveCategory } from "@/types";

const STATUS_OPTIONS = OBJECTIVE_STATUSES.map((s) => ({ value: s, label: STATUS_CONFIG[s].label }));

type ViewMode = "all" | "my";

interface OkrFiltersProps {
  search: string;
  onSearchChange: (value: string) => void;
  statusFilter: string[];
  onStatusFilterChange: (value: string[]) => void;
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
  cyclePid?: string;
  onCycleChange?: (cyclePid: string) => void;
  category?: ObjectiveCategory | "";
  onCategoryChange?: (category: ObjectiveCategory | "") => void;
}

export function OkrFilters({
  search,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  viewMode = "all",
  onViewModeChange,
  cyclePid = "",
  onCycleChange,
  category = "",
  onCategoryChange,
}: OkrFiltersProps) {
  const { data: cycles = [] } = useOkrCycles();
  const hasActiveFilters = statusFilter.length > 0;

  const toggleStatus = (status: string) => {
    if (statusFilter.includes(status)) {
      onStatusFilterChange(statusFilter.filter((s) => s !== status));
    } else {
      onStatusFilterChange([...statusFilter, status]);
    }
  };

  return (
    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:flex-wrap">
      {/* View mode toggle */}
      {onViewModeChange && (
        <div className="flex rounded-md border">
          <Button
            variant={viewMode === "all" ? "default" : "ghost"}
            size="sm"
            className={cn("gap-1.5 rounded-r-none border-0", viewMode !== "all" && "text-muted-foreground")}
            onClick={() => onViewModeChange("all")}
          >
            <Users className="h-3.5 w-3.5" /> All OKRs
          </Button>
          <Button
            variant={viewMode === "my" ? "default" : "ghost"}
            size="sm"
            className={cn("gap-1.5 rounded-l-none border-0", viewMode !== "my" && "text-muted-foreground")}
            onClick={() => onViewModeChange("my")}
          >
            <User className="h-3.5 w-3.5" /> My OKRs
          </Button>
        </div>
      )}

      {/* Cycle filter */}
      {onCycleChange && (
        <Select value={cyclePid || "all"} onValueChange={(v) => onCycleChange(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[140px] gap-1.5">
            <Calendar className="h-3.5 w-3.5" />
            <SelectValue placeholder="All cycles" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All cycles</SelectItem>
            {cycles.map((c) => (
              <SelectItem key={c.pid} value={c.pid}>
                Q{c.quarter} {c.year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {/* Category lane */}
      {onCategoryChange && (
        <Select value={category || "all"} onValueChange={(v) => onCategoryChange(v === "all" ? "" : (v as ObjectiveCategory))}>
          <SelectTrigger className="w-[140px] gap-1.5">
            <Tag className="h-3.5 w-3.5" />
            <SelectValue placeholder="All categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {OBJECTIVE_CATEGORIES.map((c) => (
              <SelectItem key={c} value={c}>{CATEGORY_LABEL[c]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {/* Search */}
      <div className="relative w-full sm:w-64">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search objectives..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Status filter */}
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className={cn("gap-2", hasActiveFilters && "border-primary")}>
            <Filter className="h-4 w-4" />
            Status
            {hasActiveFilters && (
              <Badge className="ml-1 h-5 w-5 rounded-full p-0 text-[10px]">
                {statusFilter.length}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-48 p-3" align="start">
          <div className="space-y-2">
            {STATUS_OPTIONS.map((option) => {
              const bgClass = STATUS_CONFIG[option.value as keyof typeof STATUS_CONFIG]?.dotColor || "bg-muted-foreground";
              return (
                <label key={option.value} className="flex items-center gap-2 cursor-pointer text-sm">
                  <Checkbox
                    checked={statusFilter.includes(option.value)}
                    onCheckedChange={() => toggleStatus(option.value)}
                  />
                  <span className={cn("h-2 w-2 rounded-full", bgClass)} />
                  {option.label}
                </label>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>

      {/* Clear filters */}
      {(search || hasActiveFilters || viewMode !== "all" || cyclePid || category) && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            onSearchChange("");
            onStatusFilterChange([]);
            onViewModeChange?.("all");
            onCycleChange?.("");
            onCategoryChange?.("");
          }}
          className="gap-1 text-muted-foreground"
        >
          <X className="h-3 w-3" /> Clear
        </Button>
      )}
    </div>
  );
}
